import { isBoolean, isDate, isNull, isString, isUndefined, sample } from 'es-toolkit'
import { isNumber } from 'es-toolkit/compat'
import type { Dayjs } from 'dayjs'
import dayjs from 'dayjs'
import type { ContentLayout } from '~/types/layouts'
import type { MaybeArray, NullableBoolean, SafeNullableBoolean } from '~/types/utils'
import type { AccordionItemContent, OptionsMap } from '~/types/elementsUi'
import LocalizedFormat from 'dayjs/plugin/localizedFormat'
import updateLocale from 'dayjs/plugin/updateLocale'
import 'dayjs/locale/nl'
import 'dayjs/locale/en'
import type { MaybeRefOrGetter } from 'vue'
import { match, P } from 'ts-pattern'
import {
  type ResponsiveAltTag,
  type ImageBreakpointType,
  imageBreakpointTypes,
  type ImageProps,
  type ResponsiveImageSource,
  type LocalizedAltText
} from '~/types/images'
import { heroImages } from '#shared/models/images'
import { type Gender, GenderEnum } from '#shared/types/users.ts'

dayjs.extend(LocalizedFormat)
dayjs.extend(updateLocale)

type ObjectType = string | number | boolean | any[] | Date | null | undefined
type StringObjectMapping = {
  [key: string]: ObjectType | StringObjectMapping
}
type FlatObject = { [key: string]: ObjectType }

const typeCheck = (object: ObjectType | StringObjectMapping): object is ObjectType => {
  return (
    isNumber(object) ||
    isString(object) ||
    isBoolean(object) ||
    isDate(object) ||
    Array.isArray(object) ||
    isNull(object) ||
    isUndefined(object)
  )
}

/**
 * Returns an array of full key paths, e.g. "cook.userProfile.user.name" of a (nested) object
 * @param object
 * @param isAbsolute
 */
export function getObjectKeys(
  object: ObjectType | StringObjectMapping,
  isAbsolute: boolean = true
): string[] {
  const result: string[] = []

  if (typeCheck(object)) {
    return [object?.toString() ?? 'null']
  }

  Object.keys(object).forEach((key: string) => {
    const value = object[key]

    if (typeCheck(value)) {
      result.push(key)
    } else {
      const recursiveResult = getObjectKeys(value, isAbsolute)

      recursiveResult.forEach((keyString) => {
        if (isAbsolute) {
          result.push(`${key}.${keyString}`)
        } else {
          result.push(keyString)
        }
      })
    }
  })
  return result
}

// export function getEnumKeys<T>(currentEnum: Enum): T[] {
//   return Object.values(currentEnum).filter((value) => typeof value === 'string') as T[]
// }

export function formatDate(
  dateRef: MaybeRefOrGetter<Dayjs | null | undefined>
): undefined | string {
  const date = toValue(dateRef)
  if (!date) {
    return undefined
  }

  return dayjs(date).format('DD-MM-YYYY')
}

export function formatDateAndTime(
  dateRef: MaybeRefOrGetter<Dayjs | null | undefined>
): undefined | string {
  const date = toValue(dateRef)
  if (!date) {
    return undefined
  }

  return dayjs(date).tz('Europe/Amsterdam').format('DD-MM-YYYY HH:mm')
}

export function formatDateAndAge(
  dateRef: MaybeRefOrGetter<Dayjs | null | undefined>
): undefined | string {
  const date = toValue(dateRef)
  if (!date) {
    return undefined
  }

  return `${dayjs(date).format('DD-MM-YYYY')} (${dayjs.utc().diff(date, 'year')})`
}

export function formatTime(dateRef: MaybeRefOrGetter<Dayjs>) {
  const date = toValue(dateRef)
  if (!date) {
    return undefined
  }
  return dayjs(date).tz('Europe/Amsterdam').format('HH:mm')
}

export function isContentArray(
  content: string | string[] | AccordionItemContent[]
): content is AccordionItemContent[] {
  return Array.isArray(content) && content.length > 0 && typeof content[0] !== 'string'
}

export function isList(item: string | ContentLayout): item is ContentLayout {
  return typeof item !== 'string'
}

export function textToKebabCase<T extends string>(word: T) {
  return (
    word
      // Replace spaces, underscores, or multiple hyphens with a single hyphen
      .replace(/\s+|_+|-+/g, '-')
      .toLowerCase()
  )
}

export function convertToDateObj(year: number, month: number) {
  return dayjsUTC().year(year).month(month).toDate()
}

export function localizeMonths(): string[] {
  return dayjs.updateLocale(useLocaleStore().locale, {
    months: useLocaleStore().localeIsDutch
      ? [
          'januari',
          'februari',
          'maart',
          'april',
          'mei',
          'juni',
          'juli',
          'augustus',
          'september',
          'oktober',
          'november',
          'december'
        ]
      : [
          'january',
          'february',
          'march',
          'april',
          'may',
          'june',
          'july',
          'august',
          'september',
          'october',
          'november',
          'december'
        ]
  }).months as string[]
}

export function localizeDate(date: Dayjs, localizedFormat: string) {
  return dayjs(date).locale(useLocaleStore().locale).format(localizedFormat)
}

export function scrollIntoView(elementId: string, center: boolean = false) {
  const element = document.getElementById(elementId)

  if (!element) {
    return undefined
  } else {
    element.scrollIntoView({
      block: center ? 'center' : 'start',
      behavior: getAnimationBehaviour()
    })
  }
}

export function convertStringsToBools(section: FlatObject) {
  return Object.entries(section).map(([_k, value]) => {
    if (value === 'true') {
      return true
    }
    if (value === 'false') {
      return false
    }
    return value
  })
}

export function uppercaseFirstChar(str: string) {
  return str.charAt(0).toUpperCase() + str.slice(1)
}

export function lowercaseFirstChar(str: string) {
  return str.charAt(0).toLowerCase() + str.slice(1)
}

export function removeWhitespaceFromString(str: string | undefined) {
  if (!str) {
    return
  }

  return str.replace(/\s/g, '')
}

export function replaceDashWithWhitespace(str: string) {
  return str.replace(/-/g, ' ')
}

export function shortenFullName(str: string) {
  const parts = str.split(' ')

  if (parts.length < 2) {
    return str
  }

  const firstName = parts[0]

  const lastName = parts.at(-1)!
  const lastNameInitial = `${lastName.charAt(0).toUpperCase()}.`

  return `${firstName} ${lastNameInitial}`
}

export function generateRandomString(length: number) {
  let result = ''
  const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789'
  const charactersLength = characters.length
  let counter = 0
  while (counter < length) {
    result += characters.charAt(Math.floor(Math.random() * charactersLength))
    counter += 1
  }
  return result
}

export function convertNullableBoolean(
  nullableBoolean: NullableBoolean
): SafeNullableBoolean {
  if (nullableBoolean === null) {
    return 2
  }

  return Number(nullableBoolean) as 0 | 1
}

export function getRangeOfNumbers(range: number[]): OptionsMap[] {
  return range.map((number) => {
    return {
      id: number,
      name: number.toString()
    } satisfies OptionsMap
  })
}

export async function asyncEvery<T>(
  list: T[],
  callback: (value: T) => Promise<boolean>
): Promise<boolean> {
  return await new Promise<boolean>(
    // oxlint-disable
    async (resolve) => {
      // oxlint-enable
      const promises: Promise<boolean>[] = list.map<Promise<boolean>>(async (value) => {
        const result = await callback(value)
        if (!result) {
          resolve(false)
        }
        return result
      })

      const isValid = await Promise.all(promises)
      resolve(isValid.every((result) => result))
    }
  )
}

export async function asyncSome<T>(
  list: T[],
  callback: (value: T) => Promise<boolean>
): Promise<boolean> {
  return await new Promise<boolean>(
    // oxlint-disable
    async (resolve) => {
      // oxlint-enable
      const promises = list.map<Promise<boolean>>(async (value) => {
        const result = await callback(value)
        if (result) {
          resolve(true)
        }
        return result
      })

      const isValid = await Promise.all(promises)
      resolve(isValid.some((result) => result))
    }
  )
}

export async function asyncFilter<T>(
  list: T[],
  callback: (value: T) => Promise<boolean>
): Promise<T[]> {
  return await new Promise<T[]>(
    // oxlint-disable
    async (resolve) => {
      // oxlint-enable
      const promises = list.map<Promise<T | undefined>>(async (value) => {
        const isValid = await callback(value)
        return isValid ? value : undefined
      })

      const result = await Promise.all(promises)
      const filteredList = result.filter((value) => !!value) as T[]
      resolve(filteredList)
    }
  )
}

export function getGenderName(genderRef: MaybeRefOrGetter<Gender | undefined>): string {
  const gender = toValue(genderRef)

  const localeIsDutch = useLocaleStore().localeIsDutch

  if (!gender) {
    return localeIsDutch ? 'Onbekend' : 'Unknown'
  }

  return match(gender)
    .returnType<string>()
    .with(GenderEnum.Male, () => (localeIsDutch ? 'Man' : 'Male'))
    .with(GenderEnum.Female, () => (localeIsDutch ? 'Vrouw' : 'Female'))
    .with(GenderEnum.PreferNotToSay, () =>
      localeIsDutch ? 'Deel ik liever niet' : 'Prefer not to say'
    )
    .with(P.string, (val) => val)
    .exhaustive()
}

export function toTitleCase(str: string) {
  return str.replace(/\w\S*/g, (text) => {
    if (text === 'aan') {
      return 'aan'
    }
    return text.charAt(0).toUpperCase() + text.substring(1).toLowerCase()
  })
}

export async function copyStringToClipBoard(data?: string | null) {
  if (!data) {
    return false
  }
  const result: PermissionStatus | 'firefox' = await navigator.permissions
    .query({ name: 'clipboard-write' as PermissionName })
    .catch(() => {
      return 'firefox'
    })

  if (result === 'firefox' || result?.state === 'granted' || result?.state === 'prompt') {
    await navigator.clipboard.writeText(data)
    return true
  }

  return false
}

export function getImageSource(
  imageSrc: ResponsiveImageSource,
  breakpointType: ImageBreakpointType = 'desktop'
): string {
  return typeof imageSrc === 'object' ? imageSrc[breakpointType] : imageSrc
}

function isBreakpointAlt(
  altTag: ResponsiveAltTag
): altTag is Record<ImageBreakpointType, LocalizedAltText> {
  return imageBreakpointTypes.some((breakpoint) => breakpoint in altTag)
}

export function getImageAlt(
  altTag: ResponsiveAltTag,
  breakpointType: ImageBreakpointType = 'desktop'
): string {
  const localized = isBreakpointAlt(altTag) ? altTag[breakpointType] : altTag
  return localized[useLocaleStore().locale]
}

export function getRandomHeroImage(path: string): Ref<ImageProps> {
  return useState(`hero:${path}`, () => {
    return sample(Object.values(heroImages)) ?? heroImages['app-contact']
  })
}

export function toArray<T>(value: MaybeArray<T>): T[] {
  return Array.isArray(value) ? value : [value]
}

export async function getBrowserInfo() {
  const info: Record<string, unknown> = {
    userAgent: navigator.userAgent,
    language: navigator.language,
    languages: navigator.languages,
    cookieEnabled: navigator.cookieEnabled,
    onLine: navigator.onLine,
    platform: navigator.platform,
    // Screen info
    screenWidth: screen.width,
    screenHeight: screen.height,
    colorDepth: screen.colorDepth,
    // Viewport info
    viewportWidth: window.innerWidth,
    viewportHeight: window.innerHeight,
    // Timezone
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
  }

  // Try to get Client Hints data if available
  if ('userAgentData' in navigator) {
    try {
      // TODO: Fix once TypeScript shows this properly
      const userAgentData = navigator.userAgentData as any
      const uaData = await userAgentData.getHighEntropyValues([
        'architecture',
        'bitness',
        'model',
        'platformVersion',
        'uaFullVersion',
        'fullVersionList'
      ])

      info.clientHints = {
        brands: userAgentData.brands,
        mobile: userAgentData.mobile,
        platform: userAgentData.platform,
        ...uaData
      }
    } catch (error) {
      console.warn('Could not retrieve high entropy values:', error)
    }
  }

  return info
}
